/* eslint-disable no-nested-ternary */
import {css, useMediaQuery, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DlMuiButton,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  LightSplitLayoutProps,
  LoadingSpinner,
  asQueryString,
  enumContainer,
  isNullsy,
  mixins,
  omitNullsy,
  useLocalStorage,
  useLocationQuery,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  DEFAULT_PAGE_SIZE,
  Formatters,
  HaidiSchema,
  JustTooltipFormatter,
  LightDateRangeFilter,
  LightOrganizationFilter2,
  LightFilterPanel,
  LightSelectFilter,
  LightSelectedFilters,
  LightSelectedFiltersPane,
  LazyMaterialTable,
  TableSorting,
  UserFilterDto,
  getDefaultMTOptions,
  useAtbList,
  useFinding,
  useFormatters,
  useOrganizations,
  useSnomedLabels,
  useTableData,
  useTableParams,
  useUserFilters,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {format} from 'date-fns';
import {isEqual, sortBy, throttle} from 'lodash';
import {Column} from 'material-table';
import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import {Link} from 'react-router-dom';

import {AtbCell, AntibiogramTooltipBoundary} from './antibiogramCells';
import {useAppSelector} from 'src/js/store_hooks';

// React.memo so a parent re-render with no actual prop change does NOT re-render the whole table body.
// Works only because every prop passed below is referentially stable (memoized / ref-stable callbacks).
const MemoLazyMaterialTable = React.memo(LazyMaterialTable);

// Used to pre-estimate the lazy table's first-render row count from the panel height. Must match the
// `estimatedRowHeight` passed to the table; the buffer over-estimates (covers header/legend chrome) so the
// first render over-fills rather than under-fills.
const ANTIBIOGRAM_ROW_HEIGHT = 29;
const ANTIBIOGRAM_FIT_BUFFER = 3;
// Only used if the panel can't be measured (shouldn't happen) — a generous count so the table still mounts.
const ANTIBIOGRAM_FALLBACK_INITIAL_ROWS = 24;

type AntibiogramFilters = {
  dateFrom: string | undefined;
  dateTo: string | undefined;
  floatingShortcutName: ShortcutEnum | null;
  specimenTypes: string[];
  organizationIds: string[];
  measureType: string;
  pathogenIds?: number[];
  atbCodes?: number[];
};
type FiltersData = {
  antibiogramOpen: boolean;
  antibiogram: AntibiogramFilters;
  antibiogramSorting: TableSorting;
};
const DEFAULT_ANTIBIOGRAM_FILTERS_OPEN = true;
// Default displayed timespan: rolling last 12 months. Mirrors the LAST_YEAR shortcut so the
// pre-filled range and the shortcut's (re)computed range agree and no spurious filter diff
// appears on load (PDH-3087). Computed lazily so the range stays current across days.
function getDefaultAntibiogramFilters() {
  const today = new Date();
  const lastYear = new Date(today);
  lastYear.setFullYear(today.getFullYear() - 1);
  return {
    measureType: 'ALL_ISOLATES',
    dateFrom: format(lastYear, 'yyyy-MM-dd'),
    dateTo: format(today, 'yyyy-MM-dd'),
    floatingShortcutName: ShortcutEnum.LAST_YEAR,
  };
}
const DEFAULT_ANTIBIOGRAM_SORTING = {
  field: 'testedIsolates',
  direction: 'desc',
};

type RowId = number; // pathogenCode
type RowType = HaidiSchema.components['schemas']['PathogenResultDto'];
type ExtraColumnId = number; // atbCode
type ExtraColumnData = RowType['atbResults'][0];
const PROGRESS_COLOR_MAP: Record<ExtraColumnData['susceptibilityResult'], string> = {
  LESS_THAN_5_SAMPLES: 'var(--dlThemeGray-300)',
  RANGE_0_70: '#FFD6D6',
  RANGE_70_90: '#FFEDD2',
  RANGE_90_100: '#CDFBE0',
};
function getPathogenLabel(pathogenCode: number, snomedLabels: Record<string, string>): string {
  return snomedLabels[pathogenCode] || String(pathogenCode);
}
function getExtraColumnTitle(extraColumnId: ExtraColumnId, snomedLabels: Record<string, string>): string {
  return snomedLabels[extraColumnId] || String(extraColumnId);
}
// Sorting is owned here (not by material-table) so the data can be sorted BEFORE LazyMaterialTable slices
// it — otherwise material-table would sort only the visible prefix. Mirrors the former per-column
// customSort comparators (pathogen by SNOMED label, isolates numerically).
function sortAntibiogramData(rows: RowType[], sorting: TableSorting, snomedLabels: Record<string, string>): RowType[] {
  const {field, direction} = sorting ?? {};
  if (!field || !direction) return rows;
  const dir = direction === 'desc' ? -1 : 1;
  const compare = (a: RowType, b: RowType): number => {
    if (field === 'pathogenCode') {
      const ka = getPathogenLabel(a.pathogenCode, snomedLabels);
      const kb = getPathogenLabel(b.pathogenCode, snomedLabels);
      return (+(ka > kb) - +(ka < kb)) * dir;
    }
    if (field === 'testedIsolates') {
      const ka = +(a.testedIsolates ?? 0);
      const kb = +(b.testedIsolates ?? 0);
      return (+(ka > kb) - +(ka < kb)) * dir;
    }
    return 0;
  };
  return [...rows].sort(compare);
}
function buildColumns(
  formatters: Formatters,
  defaultSort: TableSorting,
  extraColumns: ExtraColumnId[],
  extraData: Record<RowId, Record<ExtraColumnId, ExtraColumnData>>,
  snomedLabels: Record<string, string>,
  filters: AntibiogramFilters
): Column<RowType>[] {
  const EMPTY = '-';
  const {t, formatFloat} = formatters;
  // const xlOrLarger = useXlOrLarger();
  const columns: Column<RowType>[] = [
    {
      field: 'pathogenCode',
      title: t('antibiogram.pathogenCode'),
      render: (data) => {
        const pathogenLabel = getPathogenLabel(data?.pathogenCode, snomedLabels);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={pathogenLabel || EMPTY}
            tooltipOverride={pathogenLabel}
            singleLine
            route={
              data?.pathogenCode
                ? `/pathogenDetail?${asQueryString({
                    pathogen: {
                      dateFrom: filters.dateFrom,
                      dateTo: filters.dateTo,
                      specimenTypes: filters.specimenTypes,
                      organizationIds: filters.organizationIds,
                      measureType: filters.measureType,
                      pathogenIds: [data.pathogenCode],
                      atbCodes: filters.atbCodes,
                    },
                  })}`
                : undefined
            }
          />
        );
      },
      sorting: true,
      // Fixed width sized to fit roughly "Staphylococcus aureus" without ellipsis.
      width: '190px',
      // Sorting is owned by the parent (data is pre-sorted before slicing for LazyMaterialTable), so the
      // column's own sort is a no-op — the header still toggles the indicator via controlledSort/onOrderChange.
    },
    {
      field: 'testedIsolates',
      title: t('antibiogram.testedIsolates'),
      tooltip: t('antibiogram.testedIsolates.tooltip'),
      render: (data) => (
        <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data?.testedIsolates ?? 0} tooltipOverride={data?.testedIsolates ?? 0} />
      ),
      sorting: true,
      width: '66px',
      // Sorting owned by the parent (see pathogenCode column) — no-op here.
    },
    ...extraColumns.map((extraColumnId) => {
      return {
        field: `atbCode.${extraColumnId}`,
        title: getExtraColumnTitle(extraColumnId, snomedLabels),
        render: (data) => {
          const extraValue = extraData?.[data.pathogenCode]?.[extraColumnId] as ExtraColumnData | undefined;
          const progressColor =
            PROGRESS_COLOR_MAP[extraValue?.susceptibilityResult ?? 'LESS_THAN_5_SAMPLES'] ?? PROGRESS_COLOR_MAP.LESS_THAN_5_SAMPLES;
          const susceptibilityPercent = (extraValue?.susceptibilityPercentage ?? 0) * 100;
          const isMissing = isNullsy(extraValue);
          return (
            <AtbCell
              progressPercent={isMissing ? 0 : 100}
              background={progressColor}
              value={isMissing ? '' : isNullsy(extraValue?.susceptibilityPercentage) ? EMPTY : `${formatFloat(susceptibilityPercent, 0)}%`}
              tooltip={isMissing ? '' : `${extraValue?.susceptibleIsolates ?? EMPTY} / ${extraValue?.testedIsolates ?? EMPTY}`}
            />
          );
        },
        // All antibiotic columns share a fixed 58px width so the susceptibility chips render
        // at a uniform 34×26 (58px − 2×12px cell padding). Previously the last column was widened
        // by 12px, which — because ProgressBarFormatter's chip fills 100% of the cell — stretched
        // that column's chip to 46px instead of adding empty trailing space (PDH-3081).
        width: '58px',
      } as Column<RowType>;
    }),
  ];
  // Bake the active sort into the matching column so material-table marks/applies the default sort on
  // mount. `defaultSort` is the value-stable controlledSort, so columns stay referentially stable across
  // same-value sort write-backs (only an actual sort-value change rebuilds them).
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort: v.customSort ?? (() => 0),
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

function useFiltersInitSizes() {
  const smallerThan1280 = useMediaQuery('(max-width:1280px)', {noSsr: true});
  const smallerThan1440 = useMediaQuery('(max-width:1440px)', {noSsr: true});
  if (smallerThan1280) return [50, 66];
  if (smallerThan1440) return [29, 87];
  return [16, 100];
}

interface Props {
  config: ConfigObjectType;
}
export function normalizeAntibiogramFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.floatingShortcutName) {
    delete (copy as any).dateFrom;
    delete (copy as any).dateTo;
  }
  return copy;
}
export const AntibiogramLayoutContainer: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'antibiogram';
  const storageIdPrefix = '/antibiogram';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(() => {
    return {
      antibiogramOpen: query?.antibiogramOpen ?? DEFAULT_ANTIBIOGRAM_FILTERS_OPEN,
      antibiogram: query?.antibiogram ?? getDefaultAntibiogramFilters(),
      antibiogramSorting: query?.antibiogramSorting ?? DEFAULT_ANTIBIOGRAM_SORTING,
    };
  }, [query]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({antibiogram: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <AntibiogramLayoutContainerInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};
AntibiogramLayoutContainer.displayName = 'AntibiogramLayoutContainer';

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Record<string, any>) => void;
};
const AntibiogramLayoutContainerInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  // Compute once per mount so the "is anything filtered?" comparison and the reset target
  // use the same default (incl. the rolling 1-year date range) as the initial filters.
  const defaultAntibiogramFilters = useMemo(() => getDefaultAntibiogramFilters(), []);
  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scrollY`, [0, 0]);

  const antibiogramUrl = config.service.url.antibiogram;
  const antibiogramParams = useTableParams(
    {
      dateFrom: filters.antibiogram.dateFrom,
      dateTo: filters.antibiogram.dateTo,
      floatingShortcutName: filters.antibiogram.floatingShortcutName,
      specimenTypes: filters.antibiogram.specimenTypes,
      organizationIds: filters.antibiogram.organizationIds,
      measureType: filters.antibiogram.measureType,
      pathogenIds: filters.antibiogram.pathogenIds,
      atbSnomedCodes: filters.antibiogram.atbCodes,
    }
    // filters.antibiogramSorting, // NOTE: we sort only on FE
    // {}
  );
  const {data: _data, isLoading, totalCount} = useTableData<any, RowType[]>(antibiogramUrl, antibiogramParams, 'content');
  const responseData = useMemo(() => _data ?? [], [_data]);
  // Filter selectors:
  //  - pathogenIds filter → render only the selected pathogens (rows), synthesizing empty rows
  //    if BE returned no record for one of them
  //  - atbCodes filter → render only the selected antibiotics (columns), keeping the column
  //    in place even when no row has data for it
  // Pre-existing behavior is preserved when both filters are empty: rows come from BE response,
  // columns are the union of antibiotics present in `atbResults`.
  const {antibiogramData, extraColumns, extraData} = useMemo(() => {
    // Filter values may come back as strings after URL roundtrip; pathogenCode / atbCode in BE
    // response are numbers. Normalize selections to numbers so identity comparisons work.
    const selectedPathogens = (filters.antibiogram.pathogenIds ?? []).map((v) => Number(v));
    const selectedAtbs = (filters.antibiogram.atbCodes ?? []).map((v) => Number(v));
    const rows: RowType[] =
      selectedPathogens.length > 0
        ? selectedPathogens.map((code) => {
            const existing = responseData.find((row) => Number(row.pathogenCode) === code);
            return existing ?? {pathogenCode: code, testedIsolates: 0, atbResults: []};
          })
        : responseData;
    const naturalAtbs = new Set<ExtraColumnId>();
    const extraData: Record<RowId, Record<ExtraColumnId, ExtraColumnData>> = {};
    rows.forEach((row) => {
      const extraMap: Record<ExtraColumnId, ExtraColumnData> = {};
      (row?.atbResults ?? []).forEach((extraValue) => {
        naturalAtbs.add(extraValue.atbCode);
        extraMap[extraValue.atbCode] = extraValue;
      });
      extraData[row.pathogenCode] = extraMap;
    });
    const extraColumns = selectedAtbs.length > 0 ? selectedAtbs : Array.from(naturalAtbs);
    return {antibiogramData: rows, extraColumns, extraData};
  }, [responseData, filters.antibiogram.pathogenIds, filters.antibiogram.atbCodes]);
  const {snomedLabels, snomedLabelsLoading} = useSnomedLabels(config);
  const sortedExtraColumns = useMemo(() => sortBy(extraColumns, (v) => getExtraColumnTitle(v, snomedLabels)), [extraColumns, snomedLabels]);

  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  // Stabilize controlledSort by VALUE. On mount the table writes the active sort back to the URL, which
  // yields a new object with the same field/direction; keying on the primitives keeps the reference
  // stable so that write-back doesn't make material-table re-sort (and re-render all 1806 cells).
  const controlledSort = useMemo(
    () => filters.antibiogramSorting,
    [filters.antibiogramSorting?.field, filters.antibiogramSorting?.direction]
  );
  // Memoize columns so MaterialTable receives a STABLE reference across unrelated re-renders. A fresh
  // columns array each render makes material-table re-initialize the whole body (PDH perf). `defaultSort`
  // is baked from the value-stable `controlledSort`, so material-table marks/applies the default sort on
  // mount, yet columns only rebuild when the sort VALUE actually changes (not on a same-value URL write).
  const columns = useMemo(
    () => buildColumns(formatters, controlledSort, sortedExtraColumns, extraData, snomedLabels, filters.antibiogram),
    [formatters, controlledSort, sortedExtraColumns, extraData, snomedLabels, filters.antibiogram]
  );
  // Pre-sort the full dataset here (sorting is owned by us, not material-table) so LazyMaterialTable can
  // slice the correct top-N. Recomputes only on actual sort-value / data / label change.
  const sortedAntibiogramData = useMemo(
    () => sortAntibiogramData(antibiogramData, controlledSort, snomedLabels),
    [antibiogramData, controlledSort, snomedLabels]
  );
  const tableSortKey = `${controlledSort?.field ?? ''}:${controlledSort?.direction ?? ''}`;

  // Every prop handed to MaterialTable is kept referentially STABLE so the table only re-initializes
  // when the underlying data/columns actually change — not on every incidental parent re-render.
  const tableOptions = useMemo(() => ({...getDefaultMTOptions(DEFAULT_PAGE_SIZE), paging: false}), []);
  // `defaultScroll` is only the INITIAL scroll-restore position. Capture it ONCE: if this prop changed
  // on every scroll (we persist scroll below), the memoized table would re-render on every scroll tick.
  const initialScrollRef = useRef({toX: defaultScroll[0], to: defaultScroll[1]});
  const defaultScrollForTable = initialScrollRef.current;
  // Callbacks read the latest columns/setters through refs so their identity never changes.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const changeFiltersRef = useRef(changeFilters);
  changeFiltersRef.current = changeFilters;
  const setDefaultScrollRef = useRef(setDefaultScroll);
  setDefaultScrollRef.current = setDefaultScroll;
  const sortingRef = useRef(filters.antibiogramSorting);
  sortingRef.current = filters.antibiogramSorting;
  const handleOrderChange = useCallback((colIndex: number, direction: 'asc' | 'desc' | '') => {
    const field = columnsRef.current[colIndex]?.field as string | undefined;
    if (!field) return;
    // Guard: material-table fires onOrderChange with the already-active sort right after mounting.
    // Writing that identical sort back to the URL would needlessly re-render the container. Skip it.
    const {current} = sortingRef;
    if (current?.field === field && current?.direction === direction) return;
    changeFiltersRef.current({antibiogramSorting: {field, direction}});
  }, []);
  // Persist scroll position (for restore on next mount) but THROTTLED, so a scroll doesn't fire a state
  // update on every tick. Combined with the stable defaultScrollForTable above, scrolling no longer
  // re-renders the table.
  const saveScroll = useRef(
    throttle((x: number, y: number) => {
      setDefaultScrollRef.current([x, y]);
    }, 250)
  ).current;
  const handleScroll = useCallback(
    (event: Event) => {
      const el = event.target as HTMLDivElement;
      saveScroll(el.scrollLeft, el.scrollTop);
    },
    [saveScroll]
  );
  // Render the table only once everything it needs is ready (data + SNOMED labels). This avoids the
  // intermediate "render with data, then re-render once labels arrive" passes that each rebuilt the
  // whole 1806-cell body.
  const tableReady = !isLoading && !snomedLabelsLoading;

  // Measure the table area's height BEFORE the lazy table mounts, so its first render already fills the
  // viewport (no floating spinner + staged "load more"). We measure the split-layout PANEL that owns the
  // height (`antibiogram-list-wrapper`'s parent) — it is content-independent (sized by the split layout,
  // stable even while the data is still loading), unlike the content-sized wrapper itself. The count
  // deliberately OVER-estimates (ignores header/legend chrome) so it can only over-fill; LazyMaterialTable's
  // measureFit then shrinks it to the exact fit. Measured ONCE and frozen (it feeds the table's initial
  // useState only; a changing value would needlessly re-render the memoized table — resize is handled by
  // measureFit inside the table). rAF-retries until the panel is laid out.
  const [initialRowCount, setInitialRowCount] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    let raf = 0;
    let tries = 0;
    const measure = () => {
      const panel = document.querySelector('[data-name="antibiogram-list-wrapper"]')?.parentElement as HTMLElement | null | undefined;
      const h = panel?.clientHeight ?? 0;
      if (h > ANTIBIOGRAM_ROW_HEIGHT * 2) {
        setInitialRowCount(Math.ceil(h / ANTIBIOGRAM_ROW_HEIGHT) + ANTIBIOGRAM_FIT_BUFFER);
      } else if (tries++ < 30) {
        raf = requestAnimationFrame(measure); // not laid out yet — retry next frame
      } else {
        // Give up measuring (shouldn't happen) — fall back so the table still mounts (its measureFit copes).
        setInitialRowCount(ANTIBIOGRAM_FALLBACK_INITIAL_ROWS);
      }
    };
    measure();
    return () => cancelAnimationFrame(raf);
  }, []);

  const hasFilters = !isEqual(omitNullsy(filters.antibiogram), omitNullsy(defaultAntibiogramFilters));
  const hasSorting = !isEqual(omitNullsy(filters.antibiogramSorting), omitNullsy(DEFAULT_ANTIBIOGRAM_SORTING));

  const initSizes = useFiltersInitSizes();
  // Bottom-most filter-inputs row; measured by autoFitFilterRows to default the divider to 2 rows + a peek.
  const filterInputsRef = useRef<HTMLDivElement>(null);
  const splitA: LightSplitLayoutProps = {
    initSizes,
    // The 105px floor is the MIN for the OPEN filter area; when collapsed the panel is content-sized
    // (noGrow → flex:0 0 auto) and must shrink to just the headers — otherwise min-height:105 keeps the
    // card ~41px taller than its content (a big white gap below the collapsed filters).
    minSizes: [filters.antibiogramOpen ? 105 : 0, 200],
    storageId: `${storageIdPrefix}.splitA`,
    isVertical: true,
    noGrow: [!filters.antibiogramOpen, false],
    autoFitFilterRows: {contentRef: filterInputsRef, enabled: filters.antibiogramOpen},
  };
  const enumMap = useAppSelector((state) => (state[enumContainer.ENUM_REDUCER_STATE_ID] as any).enumMap);
  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const {data: organizations} = useOrganizations(config);
  const agens = useFinding(config);
  const findingOptions = useMemo(() => {
    return sortBy(
      agens.map((v) => ({id: v.code, label: v.label})),
      (v) => v.label
    );
  }, [agens]);
  const atbs = useAtbList(config);
  const atbOptions = useMemo(() => {
    return sortBy(
      atbs.map((v) => ({id: v.code, label: v.label})),
      (v) => v.label
    );
  }, [atbs]);
  const allLabResultsHref = useMemo(() => {
    // Carry over ONLY filters explicitly set by the user — never fall back to data shown in the table.
    // Antibiotics filter is intentionally NOT carried (Lab Result List does not have an antibiotics filter).
    // hasAtbSusceptibility is set to YES implicitly (only ATB-relevant results).
    return `/labs?${asQueryString({
      labs: {
        samplingDateFrom: filters.antibiogram.dateFrom,
        samplingDateTo: filters.antibiogram.dateTo,
        samplingFloatingShortcutName: filters.antibiogram.floatingShortcutName,
        specimenTypes: filters.antibiogram.specimenTypes,
        organizationIds: filters.antibiogram.organizationIds,
        findingSnomedCode: filters.antibiogram.pathogenIds,
        measureType: filters.antibiogram.measureType,
        hasAtbSusceptibility: true,
      },
    })}`;
  }, [filters.antibiogram]);

  const tableFiltersForSelectedPane = useMemo(() => {
    const hasDate = !!filters.antibiogram.dateFrom && !!filters.antibiogram.dateTo;

    const result: any = {
      ...filters.antibiogram,
    };

    if (hasDate) {
      result.date = {
        startDate: filters.antibiogram.dateFrom,
        endDate: filters.antibiogram.dateTo,
        shortcut: filters.antibiogram.floatingShortcutName,
      };
    } else {
      delete result.date;
    }

    return result;
  }, [filters.antibiogram]);

  const selectedFiltersPane =
    Object.keys(omitNullsy(tableFiltersForSelectedPane)).length > 0 ? (
      <LightSelectedFiltersPane
        selectedFilters={[
          {
            name: ['dateFrom', 'dateTo'],
            label: t('filter.antibiogram.date'),
            value: `${formatDate(filters.antibiogram.dateFrom ?? '')} - ${formatDate(filters.antibiogram.dateTo ?? '')}`,
          },
          {
            name: 'specimenTypes',
            label: t('filter.antibiogram.specimenTypes'),
            value: (filters.antibiogram.specimenTypes ?? []).map((v) => tEnum('LabReportSpecimenTypeEnum', v)).join(', '),
          },
          {
            name: 'measureType',
            label: t('filter.antibiogram.measureType'),
            value: tEnum('AntStewardshipMeasureType', filters.antibiogram.measureType),
            defaultValue: defaultAntibiogramFilters.measureType,
          },
          {
            name: 'organizationIds',
            label: t('filter.antibiogram.organizationIds'),
            value: (filters.antibiogram.organizationIds ?? [])
              .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName)
              .join(', '),
          },
          {
            name: 'pathogenIds',
            label: t('filter.pathogen'),
            value: (filters.antibiogram.pathogenIds ?? [])
              .map((id) => findingOptions.find((w) => String(w.id) === String(id))?.label)
              .join(', '),
          },
          {
            name: 'atbCodes',
            label: t('filter.antibiogram.atbCodes'),
            value: (filters.antibiogram.atbCodes ?? [])
              .map((id) => atbOptions.find((w) => String(w.id) === String(id))?.label ?? String(id))
              .join(', '),
          },
        ]}
        tableFilters={tableFiltersForSelectedPane}
        changeTableFilters={(newTableFilters) =>
          changeFilters({
            antibiogram: newTableFilters,
          })
        }
        formatters={formatters}
      />
    ) : null;
  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={`${t('navigation.section.atbReports.browser')} | ${t('antibiogram.title')}`} />
      <LightSplitLayout {...splitA}>
        <div css={muiStyles.splitContentBox}>
          <LightFilterPanel
            open={filters.antibiogramOpen}
            toggleOpen={() =>
              changeFilters({
                antibiogramOpen: !filters.antibiogramOpen,
              })
            }
            hasActiveFilter={hasFilters}
            filterInputsRef={filterInputsRef}
            savedFiltersProps={{
              storageIdPrefix,
              component,
              userFiltersLoading,
              addedUserFilters,
              userFilters,
              changeUserFilter,
              filter: filters.antibiogram,
              setFilter: (newFilter) => {
                changeFilters({
                  antibiogram: newFilter,
                });
              },
              config,
              normalizeForCompare: normalizeAntibiogramFiltersForCompare,
            }}
            selectedFilters={selectedFiltersPane}
          >
            <LightDateRangeFilter
              config={{
                name: 'date',
                ui: {label: 'filter.antibiogram.date'},
              }}
              value={
                filters.antibiogram.dateFrom && filters.antibiogram.dateTo
                  ? {
                      startDate: filters.antibiogram.dateFrom,
                      endDate: filters.antibiogram.dateTo,
                      shortcut: filters.antibiogram.floatingShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  antibiogram: {
                    ...filters.antibiogram,
                    dateFrom: newValue?.startDate,
                    dateTo: newValue?.endDate,
                    floatingShortcutName: newValue?.shortcut,
                  },
                });
              }}
              dateOnly
              showShortcuts
              isLabResults
            />
            <LightSelectFilter
              config={{
                name: `specimenTypes`,
                ui: {label: 'filter.antibiogram.specimenTypes'},
              }}
              options={sortByMessageKey(enumMap.LabReportSpecimenTypeEnum.filter((a) => a.name !== 'UNKNOWN'))}
              multi
              value={filters.antibiogram.specimenTypes}
              onChange={(newValue) => {
                changeFilters({
                  antibiogram: {
                    ...filters.antibiogram,
                    specimenTypes: newValue,
                  },
                });
              }}
            />
            <LightSelectFilter
              config={{
                name: `measureType`,
                ui: {label: 'filter.antibiogram.measureType'},
              }}
              options={sortByMessageKey(enumMap.AntStewardshipMeasureType)}
              value={filters.antibiogram.measureType}
              onChange={(newValue) => {
                changeFilters({
                  antibiogram: {
                    ...filters.antibiogram,
                    measureType: newValue,
                  },
                });
              }}
              isClearable={false}
            />
            {true && ( // TODO: multihospital?
              <LightOrganizationFilter2
                appConfig={config}
                config={{
                  name: 'organizationIds',
                  ui: {label: 'filter.antibiogram.organizationIds'},
                }}
                multi
                value={filters.antibiogram.organizationIds}
                onChange={(newOrganizationIds) => {
                  changeFilters({
                    antibiogram: {
                      ...filters.antibiogram,
                      organizationIds: newOrganizationIds,
                    },
                  });
                }}
              />
            )}
            <LightSelectFilter
              config={{
                name: `pathogenIds`,
                ui: {label: 'filter.pathogen'},
              }}
              options={findingOptions}
              multi
              value={filters.antibiogram.pathogenIds}
              onChange={(newValue) => {
                changeFilters({
                  antibiogram: {
                    ...filters.antibiogram,
                    pathogenIds: newValue,
                  },
                });
              }}
            />
            <LightSelectFilter
              config={{
                name: `atbCodes`,
                ui: {label: 'filter.antibiogram.atbCodes'},
              }}
              options={atbOptions}
              multi
              value={filters.antibiogram.atbCodes}
              onChange={(newValue) => {
                changeFilters({
                  antibiogram: {
                    ...filters.antibiogram,
                    atbCodes: newValue,
                  },
                });
              }}
            />
          </LightFilterPanel>
        </div>
        <AntibiogramTooltipBoundary data-name='antibiogram-list-wrapper' css={muiStyles.contentBox}>
          <IconHeaderWithSearch
            iconName={'listIconMultiColor'}
            label={'antibiogram.title'}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
            childrenWrapperStyle={{gap: 6}}
            betweenItems={
              <div key='legend' css={muiStyles.legendWrapper}>
                <div css={muiStyles.legendItem} style={{background: PROGRESS_COLOR_MAP.RANGE_90_100}}>
                  {t('atbResistance.legend.green')}
                </div>
                <div css={muiStyles.legendItem} style={{background: PROGRESS_COLOR_MAP.RANGE_70_90}}>
                  {t('atbResistance.legend.yellow')}
                </div>
                <div css={muiStyles.legendItem} style={{background: PROGRESS_COLOR_MAP.RANGE_0_70}}>
                  {t('atbResistance.legend.red')}
                </div>
                <div
                  css={muiStyles.legendItem}
                  style={{
                    background: PROGRESS_COLOR_MAP.LESS_THAN_5_SAMPLES,
                  }}
                >
                  {t('atbResistance.legend.grey')}
                </div>
              </div>
            }
          >
            <LightSelectedFilters
              hasSorting={hasSorting}
              handleResetSorting={() =>
                changeFilters({
                  antibiogramSorting: DEFAULT_ANTIBIOGRAM_SORTING,
                })
              }
              hasFilters={hasFilters}
              handleResetFilters={() =>
                changeFilters({
                  antibiogram: defaultAntibiogramFilters,
                })
              }
            />
            <Link tabIndex={0} to={allLabResultsHref} css={muiStyles.showAllLink}>
              <DlMuiButton
                type='chip-filled'
                title='dashboard.showAll'
                onClick={() => {
                  // noop
                }}
              />
            </Link>
          </IconHeaderWithSearch>
          {tableReady && initialRowCount != null ? (
            <MemoLazyMaterialTable
              options={tableOptions}
              variant='big'
              useMaxHeight
              columns={columns}
              singleLineColumnLabels
              verticalColumnLabels
              leftAlignColumnLabels
              fitContentWidth
              tinyRows
              lightCells
              data={sortedAntibiogramData}
              isLoading={false}
              totalCount={totalCount}
              onOrderChange={handleOrderChange}
              controlledSort={controlledSort}
              defaultScroll={defaultScrollForTable}
              onScroll={handleScroll}
              renderBatchSize={10}
              estimatedRowHeight={ANTIBIOGRAM_ROW_HEIGHT}
              initialRenderCount={initialRowCount}
              sortKey={tableSortKey}
            />
          ) : (
            <LoadingSpinner isVisible marginTop={16} />
          )}
        </AntibiogramTooltipBoundary>
      </LightSplitLayout>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  splitContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
    color: var(--dlTheme-700);
    width: 100%;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  // legend
  legendWrapper: css`
    display: inline-flex;
    background: var(--dlThemeGray-100);
    border-radius: 8px;
    padding: 8px;
    gap: 8px;
  `,
  legendItem: css`
    padding: 4px 8px;
    border-radius: 5px;
  `,
  showAllLink: css`
    text-decoration: none;
    margin-left: auto;
  `,
});
